import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { STATUS, colorFor } from "@/lib/mock";

const tooltipStyle = {
  background: "rgba(255,255,255,0.95)",
  border: "1px solid rgba(0,0,0,0.06)",
  borderRadius: 12,
  fontSize: 12,
  fontFamily: "IBM Plex Sans",
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
};

export function StatusDonut({ stats = {} }) {
  const data = [
    { name: STATUS.open.label,        value: stats.open || 0,        color: STATUS.open.color },
    { name: STATUS.assigned.label,    value: stats.assigned || 0,    color: STATUS.assigned.color },
    { name: STATUS.in_progress.label, value: stats.in_progress || 0, color: STATUS.in_progress.color },
    { name: STATUS.on_hold.label,     value: stats.on_hold || 0,     color: STATUS.on_hold.color },
    { name: STATUS.resolved.label,    value: stats.resolved || 0,    color: STATUS.resolved.color },
    { name: STATUS.closed.label,      value: stats.closed || 0,      color: STATUS.closed.color },
  ].filter((x) => x.value > 0);
  const total = data.reduce((s, x) => s + x.value, 0);
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-status-donut">
      <div className="overline">Status distribution</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Where things stand</h3>
      <div className="grid grid-cols-[160px_1fr] gap-4 mt-2 items-center">
        <div className="h-40 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={44} outerRadius={70} paddingAngle={2} stroke="none">
                {data.map((c) => <Cell key={c.name} fill={c.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center">
              <div className="metric text-2xl">{total}</div>
              <div className="overline">total</div>
            </div>
          </div>
        </div>
        <ul className="text-sm space-y-1.5">
          {data.length === 0 && <li className="text-muted-foreground text-xs">No tickets yet.</li>}
          {data.map((c) => (
            <li key={c.name} className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
              <span className="flex-1 truncate">{c.name}</span>
              <span className="text-mono text-xs text-muted-foreground">{total ? Math.round((c.value / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function CategoryDonut({ byCategory = {}, categories = [] }) {
  const catMap = Object.fromEntries(categories.map((c) => [String(c.id), c]));
  const data = Object.entries(byCategory)
    .map(([id, value]) => {
      const c = catMap[id] || { name: `#${id}` };
      return { name: c.name, value, color: colorFor(id) };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);
  const total = data.reduce((s, x) => s + x.value, 0);

  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-category-donut">
      <div className="overline">Categories · share</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">By service</h3>
      <div className="grid grid-cols-[160px_1fr] gap-4 mt-2 items-center">
        <div className="h-40 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={44} outerRadius={70} paddingAngle={2} stroke="none">
                {data.map((c) => <Cell key={c.name} fill={c.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center">
              <div className="metric text-2xl">{total}</div>
              <div className="overline">total</div>
            </div>
          </div>
        </div>
        <ul className="text-sm space-y-1.5">
          {data.length === 0 && <li className="text-muted-foreground text-xs">No data yet.</li>}
          {data.map((c) => (
            <li key={c.name} className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
              <span className="flex-1 truncate">{c.name}</span>
              <span className="text-mono text-xs text-muted-foreground">{total ? Math.round((c.value / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function AssigneeLoadChart({ byAssignee = {}, users = [] }) {
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
  const data = Object.entries(byAssignee).map(([id, val]) => ({
    name: (userMap[id]?.name || id.slice(0, 6)).split(" ")[0],
    open: val,
  })).slice(0, 8);
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-assignee-load">
      <div className="overline">Technician workload</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Open by assignee</h3>
      <div className="h-56 mt-4 -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -10, right: 10, top: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,71,255,0.05)" }} />
            <Bar dataKey="open" fill="#0047FF" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function SlaRing({ overdue = 0, total = 0 }) {
  const met = total > 0 ? Math.max(0, Math.round(((total - overdue) / total) * 100)) : 100;
  let ringColor = "#EF4444";
  if (met >= 90) ringColor = "#10B981";
  else if (met >= 75) ringColor = "#F59E0B";
  return (
    <div className="glass rounded-2xl p-5 relative overflow-hidden" data-testid="chart-sla-ring">
      <div className="overline">SLA compliance</div>
      <div className="mt-3 flex items-center gap-5">
        <div
          className="ring-conic h-28 w-28 rounded-full grid place-items-center"
          style={{ "--pct": met, "--ring-color": ringColor }}
        >
          <div className="h-[92px] w-[92px] rounded-full bg-white dark:bg-zinc-900 grid place-items-center shadow-inner">
            <div className="text-center">
              <div className="metric text-2xl">{met}%</div>
              <div className="overline">met</div>
            </div>
          </div>
        </div>
        <div className="flex-1 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Overdue</span><span className="text-mono">{overdue}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="text-mono">{total}</span></div>
        </div>
      </div>
    </div>
  );
}

export function CampusRadar({ series = [] }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-campus-line">
      <div className="overline">Multi-campus tickets</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Cross-campus load</h3>
      <div className="h-56 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ left: -10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
            <XAxis dataKey="code" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,71,255,0.05)" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="open"   name="Open"   fill="#0047FF" radius={[6, 6, 0, 0]} />
            <Bar dataKey="closed" name="Closed" fill="#22D3EE" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ============ SUPERADMIN platform charts ============ */
const PALETTE = ["#0047FF","#22D3EE","#7C3AED","#16A34A","#F59E0B","#EC4899","#06B6D4","#64748B"];

export function PlatformCategoryPie({ byCategory = [] }) {
  const data = byCategory.map((c, i) => ({ ...c, color: PALETTE[i % PALETTE.length] }));
  const total = data.reduce((s, x) => s + x.value, 0);
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-platform-category">
      <div className="overline">Across all institutes</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Tickets by category</h3>
      <div className="grid grid-cols-[160px_1fr] gap-4 mt-2 items-center">
        <div className="h-40 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={44} outerRadius={70} paddingAngle={2} stroke="none">
                {data.map((c) => <Cell key={c.name} fill={c.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center"><div className="metric text-2xl">{total}</div><div className="overline">total</div></div>
          </div>
        </div>
        <ul className="text-sm space-y-1.5">
          {data.length === 0 && <li className="text-muted-foreground text-xs">No tickets yet.</li>}
          {data.map((c) => (
            <li key={c.name} className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
              <span className="flex-1 truncate">{c.name}</span>
              <span className="text-mono text-xs text-muted-foreground">{total ? Math.round((c.value / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function TicketsPerInstitute({ byInstitute = [] }) {
  const data = byInstitute.slice(0, 8).map((i) => ({ name: i.code || i.name, open: i.open, resolved: i.resolved }));
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-tickets-per-institute">
      <div className="overline">Load distribution</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Tickets per institute</h3>
      <div className="h-56 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -10, right: 10, top: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="open"     name="Open"     fill="#0047FF" radius={[6, 6, 0, 0]} />
            <Bar dataKey="resolved" name="Resolved" fill="#22D3EE" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function PlatformGrowth({ growth = [] }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid="chart-platform-growth">
      <div className="overline">Last 6 months</div>
      <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Platform growth</h3>
      <div className="h-56 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={growth} margin={{ left: -10, right: 10, top: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="institutes" name="Institutes" stroke="#0047FF" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="users"      name="Users"      stroke="#7C3AED" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="tickets"    name="Tickets/mo" stroke="#22D3EE" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
